/**
 * BLE Device Type Detection and Constants
 * Supports both legacy proprietary meters and ACON GATT-based meters
 */

// Device type constants
export const DEVICE_TYPES = {
  LEGACY: 'LEGACY', // Original proprietary protocol (fee0 service)
  ACON: 'ACON', // ACON GATT Glucose Profile (0x1808 service)
  UNKNOWN: 'UNKNOWN',
};

// Service UUIDs for device detection
export const SERVICE_UUIDS = {
  LEGACY: 'fee0', // Legacy device service UUID
  GLUCOSE_SERVICE: '1808', // Standard GATT Glucose Service
  DEVICE_INFO_SERVICE: '180a', // Device Information Service
  CURRENT_TIME_SERVICE: '1805', // Current Time Service
  SPP_SERVICE: '11223344-5566-7788-99AA-BBCCDDEEFF00', // Proprietary transparent transmission service
};

// ACON GATT Characteristic UUIDs
export const ACON_CHARACTERISTICS = {
  // Glucose Service (0x1808)
  GLUCOSE_MEASUREMENT: '2a18', // Notify - sends glucose readings
  GLUCOSE_MEASUREMENT_CONTEXT: '2a34', // Notify - sends meal context
  GLUCOSE_FEATURE: '2a51', // Read - device features
  RACP: '2a52', // Write/Indicate - Record Access Control Point
  SPP_CHANNEL_1: '4a5b', // Write/Notify - proprietary channel
  SPP_CHANNEL_2: '4aff', // Read/Write - proprietary channel

  // Device Information Service (0x180a)
  MANUFACTURER_NAME: '2a29', // Read
  MODEL_NUMBER: '2a24', // Read
  SERIAL_NUMBER: '2a25', // Read
  HARDWARE_VERSION: '2a27', // Read
  FIRMWARE_VERSION: '2a26', // Read
  SOFTWARE_VERSION: '2a28', // Read

  // Current Time Service (0x1805)
  CURRENT_TIME: '2a2b', // Read/Write
};

// Legacy device characteristic UUIDs
export const LEGACY_CHARACTERISTICS = {
  CPL_MODE: 'fee1', // Write - enable CPL mode
  NOTIFICATION: 'fee2', // Notify - receive data
  COMMAND: 'fee3', // Write - send commands
};

// RACP Operation Codes
export const RACP_OPCODE = {
  REPORT_RECORDS: 0x01,
  DELETE_RECORDS: 0x02,
  ABORT_OPERATION: 0x03,
  REPORT_NUMBER_OF_RECORDS: 0x04,
  NUMBER_OF_RECORDS_RESPONSE: 0x05,
  RESPONSE_CODE: 0x06,
};

// RACP Operators
export const RACP_OPERATOR = {
  NULL: 0x00,
  ALL_RECORDS: 0x01,
  LESS_THAN_OR_EQUAL: 0x02,
  GREATER_THAN_OR_EQUAL: 0x03,
  RANGE: 0x04,
  FIRST: 0x05,
  LAST: 0x06,
};

// RACP Response Codes
export const RACP_RESPONSE_CODE = {
  SUCCESS: 0x01,
  OPCODE_NOT_SUPPORTED: 0x02,
  INVALID_OPERATOR: 0x03,
  OPERATOR_NOT_SUPPORTED: 0x04,
  INVALID_OPERAND: 0x05,
  NO_RECORDS_FOUND: 0x06,
  ABORT_UNSUCCESSFUL: 0x07,
  PROCEDURE_NOT_COMPLETED: 0x08,
  OPERAND_NOT_SUPPORTED: 0x09,
};

// Glucose Measurement Flags
export const GLUCOSE_MEASUREMENT_FLAGS = {
  TIME_OFFSET_PRESENT: 0x01,
  CONCENTRATION_AND_LOCATION_PRESENT: 0x02,
  CONCENTRATION_UNITS_MOL_L: 0x04, // 0 = kg/L, 1 = mol/L
  SENSOR_STATUS_PRESENT: 0x08,
  CONTEXT_INFO_FOLLOWS: 0x10,
};

// Glucose Measurement Context Flags
export const GLUCOSE_CONTEXT_FLAGS = {
  CARBOHYDRATE_PRESENT: 0x01,
  MEAL_PRESENT: 0x02,
  TESTER_HEALTH_PRESENT: 0x04,
  EXERCISE_PRESENT: 0x08,
  MEDICATION_PRESENT: 0x10,
  MEDICATION_UNITS_LITERS: 0x20,
  HBA1C_PRESENT: 0x40,
  EXTENDED_FLAGS_PRESENT: 0x80,
};

// Meal types
export const MEAL_TYPE = {
  PREPRANDIAL: 1, // Before meal
  POSTPRANDIAL: 2, // After meal
  FASTING: 3,
  CASUAL: 4,
  BEDTIME: 5,
};

/**
 * Detect device type based on advertised service UUIDs and device name
 * @param {Array} serviceUUIDs - Array of service UUID strings from BLE advertisement
 * @param {string} deviceName - Device name from BLE advertisement
 * @returns {string} Device type constant (LEGACY, ACON, or UNKNOWN)
 */
export const detectDeviceType = (serviceUUIDs, deviceName = '') => {
  if (!serviceUUIDs || !Array.isArray(serviceUUIDs)) {
    return DEVICE_TYPES.UNKNOWN;
  }

  // Normalize UUIDs to lowercase for comparison without dashes
  const normalizedUUIDs = serviceUUIDs.map(uuid =>
    uuid.toLowerCase().replace(/-/g, ''),
  );

  const matchesUuid = target => {
    const normalizedTarget = target?.toLowerCase?.().replace(/-/g, '');
    if (!normalizedTarget) {
      return false;
    }
    return normalizedUUIDs.some(
      uuid =>
        uuid === normalizedTarget ||
        (normalizedTarget.length <= uuid.length &&
          (uuid.endsWith(normalizedTarget) ||
            (normalizedTarget.length >= 4 && uuid.includes(normalizedTarget)))),
    );
  };

  const name = deviceName ? deviceName.toLowerCase() : '';

  // Check for legacy Bionime service FIRST (takes priority)
  if (matchesUuid(SERVICE_UUIDS.LEGACY)) {
    // Bionime meters typically have numeric names like "2282TAB0085"
    return DEVICE_TYPES.LEGACY;
  }

  // Check for ACON GATT Glucose Service
  // ACON meters advertise with names like "express mobile009"
  if (matchesUuid(SERVICE_UUIDS.GLUCOSE_SERVICE)) {
    // Additional validation: ACON meters have identifying name patterns or proprietary SPP service
    if (
      name.includes('express') ||
      name.includes('mobile') ||
      name.includes('acon') ||
      name.includes('diathrive') ||
      matchesUuid(SERVICE_UUIDS.SPP_SERVICE)
    ) {
      return DEVICE_TYPES.ACON;
    }

    // If device has Glucose Service (1808) but name doesn't match patterns,
    // still treat as ACON to be more lenient - user may have renamed meter or it's a variant
    return DEVICE_TYPES.ACON;
  }

  return DEVICE_TYPES.UNKNOWN;
};

/**
 * Get human-readable device type name
 * @param {string} deviceType - Device type constant
 * @returns {string} Human-readable name
 */
export const getDeviceTypeName = deviceType => {
  switch (deviceType) {
    case DEVICE_TYPES.LEGACY:
      return 'Diathrive+';
    case DEVICE_TYPES.ACON:
      return 'Diathrive Mobile';
    default:
      return 'Unknown Device';
  }
};

/**
 * Check if device supports meal context
 * @param {string} deviceType - Device type constant
 * @returns {boolean} True if device supports meal context
 */
export const supportsMealContext = deviceType => {
  return deviceType === DEVICE_TYPES.ACON;
};

/**
 * Get service UUID to scan for based on device types
 * @returns {Array} Array of service UUIDs to include in scan
 */
export const getScanServiceUUIDs = () => {
  return [SERVICE_UUIDS.LEGACY, SERVICE_UUIDS.GLUCOSE_SERVICE];
};
